import React, { useEffect, useRef } from 'preact/hooks'
import { forwardRef } from 'react'

import {
    ButtonProps,
    ButtonRefs,
    Icon
} from '@nespresso-design-system/components'

import Image from '@commons/image/Image'
import { ImageProps } from '@commons/image/types'
import { componentInteraction } from '@content/helpers/src/gtmEvents'
import { Block, ListItem } from './types'
import styles from './ModalContainer.module.scss'
import CTA from '@commons/cta/CTA.tsx'

interface ModalContainerProps {
    content: BlockContentProps
    tracking_slot: string
}

interface BlockContentProps {
    blocksDescription?: string
    firstBlock: Block
    secondBlock: Block
}

interface BlockDescriptionProps {
    description: Block['description']
    ref: HTMLParagraphElement
}

type RenderCTAProps = ButtonProps & {
    tracking_slot: string
}

const RenderList = ({
    listRef,
    ...list
}: {
    listRef: React.RefObject<HTMLUListElement>
    list: ListItem[]
}) => {
    const arr = Object.values(list)

    return (
        <ul ref={listRef}>
            {arr.map(
                (
                    { is_deactivated, icon, heading, description }: ListItem,
                    index
                ) => (
                    <li
                        key={index}
                        className={is_deactivated ? styles.isDeactivated : ''}>
                        <Icon icon={icon} method="mask" />
                        <span className={'t-sm-500'}>
                            {heading}
                            {description && (
                                <span
                                    className={`t-sm-400 ${styles.featuresListDescription}`}>
                                    {description}
                                </span>
                            )}
                        </span>
                    </li>
                )
            )}
        </ul>
    )
}

const RenderImage = ({ src, alt }: ImageProps) => {
    if (!src) {
        return null
    }

    return (
        <div className={styles.featuresImage}>
            <Image src={src} alt={alt} />
        </div>
    )
}

const RenderDescription = forwardRef<
    HTMLParagraphElement,
    BlockDescriptionProps
>(({ description }, ref) => {
    if (!description) return null

    return (
        <p ref={ref} className={styles.featuresDescription}>
            {description}
        </p>
    )
})

const RenderCTA = ({ tracking_slot, ...cta }: RenderCTAProps) => {
    const ctaRef = useRef<ButtonRefs>(null)

    if (!cta.label) {
        return null
    }

    const handleClick = () => {
        componentInteraction(tracking_slot, 'modal link click', cta.label)
    }

    return (
        <div className={styles.featuresActions}>
            <CTA
                {...cta}
                className={styles.featuresButton}
                onClick={handleClick}
                ref={ctaRef}
            />
        </div>
    )
}

export default function ModalContainer({
    content,
    tracking_slot
}: ModalContainerProps) {
    const leftListRef = useRef<HTMLUListElement>(null)
    const rightListRef = useRef<HTMLUListElement>(null)

    const titleRef1 = useRef<HTMLHeadingElement>(null)
    const titleRef2 = useRef<HTMLHeadingElement>(null)
    const descRef1 = useRef<HTMLParagraphElement>(null)
    const descRef2 = useRef<HTMLParagraphElement>(null)

    useEffect(() => {
        const adjustListsHeights = () => {
            const leftItems = leftListRef.current?.children
            const rightItems = rightListRef.current?.children

            if (
                !leftItems ||
                !rightItems ||
                leftItems.length !== rightItems.length
            ) {
                console.warn('Lists are mismatched or not rendered correctly.')
                return
            }

            for (let i = 0; i < leftItems.length; i++) {
                ;(leftItems[i] as HTMLElement).style.height = 'auto'
                ;(rightItems[i] as HTMLElement).style.height = 'auto'
            }

            for (let i = 0; i < leftItems.length; i++) {
                const leftHeight = (leftItems[i] as HTMLElement).offsetHeight
                const rightHeight = (rightItems[i] as HTMLElement).offsetHeight
                const maxHeight = Math.max(leftHeight, rightHeight)

                ;(leftItems[i] as HTMLElement).style.height = `${maxHeight}px`
                ;(rightItems[i] as HTMLElement).style.height = `${maxHeight}px`
            }
        }

        adjustListsHeights()

        window.addEventListener('resize', adjustListsHeights)

        return () => {
            window.removeEventListener('resize', adjustListsHeights)
        }
    }, [])

    useEffect(() => {
        const adjustTitleAndDescriptionHeights = () => {
            const pairs: [HTMLElement | null, HTMLElement | null][] = [
                [titleRef1.current, titleRef2.current],
                [descRef1.current, descRef2.current]
            ]

            pairs.forEach(([el1, el2]) => {
                if (el1 && el2) {
                    el1.style.height = el2.style.height = 'auto'

                    if (el1.offsetHeight !== el2.offsetHeight) {
                        const maxHeight = Math.max(
                            el1.offsetHeight,
                            el2.offsetHeight
                        )
                        el1.style.height = el2.style.height = `${maxHeight}px`
                    }
                }
            })
        }

        adjustTitleAndDescriptionHeights()
        window.addEventListener('resize', adjustTitleAndDescriptionHeights)

        return () => {
            window.removeEventListener(
                'resize',
                adjustTitleAndDescriptionHeights
            )
        }
    })

    if (!content) {
        return null
    }

    const { firstBlock, secondBlock, blocksDescription } = content

    return (
        <>
            {blocksDescription && <p>{blocksDescription}</p>}
            <div className={styles.features}>
                <div>
                    <h4 ref={titleRef1} className={styles.featuredTitle}>
                        {firstBlock.title}
                    </h4>
                    <RenderDescription
                        ref={descRef1}
                        description={firstBlock.description}
                    />
                    <RenderImage
                        src={firstBlock?.image?.desktop?.src}
                        alt={firstBlock?.image?.desktop?.alt}
                    />
                    <RenderList listRef={leftListRef} {...firstBlock.list} />
                    <RenderCTA
                        tracking_slot={tracking_slot}
                        {...firstBlock.cta}
                    />
                </div>
                <div>
                    <h4 ref={titleRef2} className={styles.featuredTitle}>
                        {secondBlock.title}
                    </h4>
                    <RenderDescription
                        ref={descRef2}
                        description={secondBlock.description}
                    />
                    <RenderImage
                        src={secondBlock?.image?.desktop?.src}
                        alt={secondBlock?.image?.desktop?.alt}
                    />
                    <RenderList listRef={rightListRef} {...secondBlock.list} />
                    <RenderCTA
                        tracking_slot={tracking_slot}
                        {...secondBlock.cta}
                    />
                </div>
            </div>
        </>
    )
}
